import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { axios } from "../..";
import FilteredTableCore from "../../components/Organismos/Table/FilteredTableCore";
import QuickFiltersBar from "../../components/Organismos/Table/QuickFiltersBar";
import type {
  IFilterData,
  IHeader,
  IHeaderCellProps,
  PanelFilterResetInfo,
} from "../../components/Organismos/Table/FilteredTable.types";
import { CUSTOM_FILTER_CHIP_ID } from "../../components/Organismos/Table/FilteredTable.types";
import DropDownMultiple, { type DropDownOptionBase } from "../../components/staff/DropDownMultiple";
import Button from "../../components/staff/Button";
import Gear from "../../components/Atomos/Icons/Gear";
import Stats from "../../components/Atomos/Icons/Stats";
import ManageFiscalYearModal from "./ManageFiscalYearModal";
import { UseRegionList } from "../../hooks/Catalogs/useRegionListOptions";
import { formatBillingPeriod } from "../../utils/functions";
import {
  formatCalendarDateToString,
  getCurrentFiscalYear,
} from "../../utils/dates";
import {
  billingPeriodRangeLabel,
  fiscalYearFromBillingPeriodStartYear,
  getDefaultAccountingBillingPeriodStartYear,
} from "../../utils/billingYear";
import {
  formatAccountingAmount,
  formatAccountingComments,
} from "../../utils/accountingFinancialDisplay";
import { formatBillingStatusLabel } from "../../utils/billingStatus";
import { resolveFirmJurisdictionDisplay } from "../../utils/resolveFirmJurisdictionDisplay";
import AccountingStats, { type AccountingTotals } from "./AccountingStats";
import { MANAGE_FIRM_EXPAND_JURISDICTIONS_BUTTON_CLASS } from "../ManageFirm/manageFirmJurisdictionsExpandButton";
import ManageFirmJurisdictionsExpandGlyph from "../ManageFirm/ManageFirmJurisdictionsExpandGlyph";
import {
  getInvoiceAggregatesByOffice,
  sumOfficeBillingTotals,
  type OfficeBillingMap,
} from "../../Services/Billing";
import {
  filterAccountingRawDataByPaymentStatus,
  matchesAccountingPaymentStatusFilter,
  officeFirmShortLabel,
  sortAccountingRawData,
  type AccountingSorting,
} from "../../utils/accountingClientSort";
import {
  ACCOUNTING_TABS,
  isAccountingTab,
  STATS_TABS,
  type AccountingTab,
} from "./accountingTabs";
import {
  ACCOUNTING_TABLE_OFFICE_VISIBILITY_FILTER,
  buildAccountingTableDisplayRows,
  fetchAccountingFirmOfficeIndex,
  formatAccountingJurisdictionCell,
  type AccountingFirmOfficeGroup,
  type AccountingTableRow,
} from "../../utils/accountingOfficeTableGrouping";

type ViewMode = "fiscalYear" | "total";

const ASSESSMENT_DUES_COLUMN_IDS = new Set([
  "assessmentDuesInvoiced",
  "assessmentDuesPaid",
]);

const ASSESSMENT_DUES_HEADER_GROUP = {
  headerGroup: "Assessment D.I.",
  headerGroupTooltip: "Assessment Dues",
  headerGroupId: "assessmentDues",
};

const INITIATION_FEES_HEADER_GROUP = {
  headerGroup: "Initiation F.",
  headerGroupTooltip: "Initiation Fees",
  headerGroupId: "initiationFees",
};

const ANNUAL_DUES_HEADER_GROUP = {
  headerGroup: "Annual D.A.",
  headerGroupTooltip: "Annual Dues Amount",
  headerGroupId: "annualDuesAmount",
};

const SPECIAL_FUND_HEADER_GROUP = {
  headerGroup: "Special F.",
  headerGroupTooltip: "Special Fund",
  headerGroupId: "specialFund",
};

const ANNUAL_MEETING_HEADER_GROUP = {
  headerGroup: "Annual Mtng R.F.",
  headerGroupTooltip: "Annual Meeting Registration Fees",
  headerGroupId: "annualMeetingRegFees",
};

const LATE_FEES_HEADER_GROUP = {
  headerGroup: "Late F.",
  headerGroupTooltip: "Late Fees",
  headerGroupId: "lateFees",
};

const BILLING_PERIOD_HEADER = {
  header: "Billing P.",
  headerTooltip: "Billing Period",
};

const PAID_AMOUNT_COLUMN = {
  width: 80,
};

function filterColumnsForTab(
  columns: IHeader[],
  tab: AccountingTab,
): IHeader[] {
  if (tab === "accountingAll") {
    return columns;
  }
  if (tab === "annualDues") {
    return columns.filter((col) => !ASSESSMENT_DUES_COLUMN_IDS.has(col.id));
  }
  return columns.filter(
    (col) => !col.headerGroup || ASSESSMENT_DUES_COLUMN_IDS.has(col.id),
  );
}

const EMPTY_TOTALS: AccountingTotals = {
  initiation: { invoiced: 0, paid: 0 },
  annualDues: { invoiced: 0, paid: 0 },
  assessmentDues: { invoiced: 0, paid: 0 },
  membershipBalance: { invoiced: 0, paid: 0 },
  specialFund: { invoiced: 0, paid: 0 },
  annualMeeting: { invoiced: 0, paid: 0 },
  lateFees: { invoiced: 0, paid: 0 },
};

// Legacy financial.asp filter options.
const MEMBERSHIP_STATUS_OPTIONS: DropDownOptionBase[] = [
  { id: "all", name: "All" },
  { id: "active", name: "Active Members" },
  { id: "withdrawn", name: "Withdrawn" },
  { id: "new", name: "New Members" },
  { id: "prospect", name: "Prospects" },
];

const PAYMENT_STATUS_OPTIONS: DropDownOptionBase[] = [
  { id: "all", name: "All" },
  { id: "paid", name: "Paid" },
  { id: "partial", name: "Partial" },
  { id: "unpaid", name: "Unpaid" },
];

// `id` maps to the office `serviceType` key stored in Cosmos; `name` is the display label.
const SERVICE_TYPE_OPTIONS: DropDownOptionBase[] = [
  { id: "accounting", name: "Accounting" },
  { id: "investmentBanking", name: "Investment Banking" },
  { id: "legal", name: "Legal Services" },
];

const BILLING_PERIOD_OPTIONS: DropDownOptionBase[] = [
  { id: "all", name: "All" },
  ...Array.from({ length: 12 }, (_, i) => {
    const m = i + 1;
    return {
      id: String(m).padStart(2, "0"),
      name: new Date(2000, i, 1).toLocaleString("en-US", { month: "long" }),
    };
  }),
];

function optionById(
  options: DropDownOptionBase[],
  id: string,
): DropDownOptionBase {
  return options.find((o) => String(o.id) === id) ?? options[0];
}

function customFilterChip(option: DropDownOptionBase): DropDownOptionBase {
  return { id: CUSTOM_FILTER_CHIP_ID, name: option.name };
}

export default function Accounting(): React.ReactElement {
  const endpoint = "/office";
  const location = useLocation();
  const currentFiscalYear = useMemo(() => getCurrentFiscalYear(), []);
  const [activeTab, setActiveTab] = useState<AccountingTab>(() => {
    const tabFromState = (location.state as { activeTab?: unknown } | null)
      ?.activeTab;
    return isAccountingTab(tabFromState) ? tabFromState : "accountingAll";
  });
  const [viewMode, setViewMode] = useState<ViewMode>("fiscalYear");
  const [showStats, setShowStats] = useState(true);
  const [manageFiscalYearOpen, setManageFiscalYearOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(() =>
    getDefaultAccountingBillingPeriodStartYear(),
  );
  const invoiceFiscalYear = useMemo(
    () => fiscalYearFromBillingPeriodStartYear(selectedYear),
    [selectedYear],
  );
  const [serverFilter, setServerFilter] = useState<Record<string, unknown>>({});
  const [totals, setTotals] = useState<AccountingTotals>(EMPTY_TOTALS);
  const [totalModeTotals, setTotalModeTotals] =
    useState<AccountingTotals>(EMPTY_TOTALS);
  const [billingByOffice, setBillingByOffice] = useState<OfficeBillingMap>({});
  const [billingByOfficeTotal, setBillingByOfficeTotal] =
    useState<OfficeBillingMap>({});
  const [activeSorting, setActiveSorting] = useState<AccountingSorting>({
    field: "jurisdiction",
    order: "asc",
  });
  const [membershipCategory, setMembershipCategory] = useState("active");
  const [paymentStatus, setPaymentStatus] = useState("all");
  const [billingMonth, setBillingMonth] = useState("all");

  const handlePanelFilterReset = useCallback(
    (resets: PanelFilterResetInfo[]) => {
      const names = new Set(resets.map((r) => r.name));
      if (names.has("Fiscal Year")) {
        setSelectedYear(getDefaultAccountingBillingPeriodStartYear());
      }
      if (names.has("Membership Status")) {
        setMembershipCategory("active");
      }
      if (names.has("Payment Status")) {
        setPaymentStatus("all");
      }
      if (names.has("Billing Period")) {
        setBillingMonth("all");
      }
    },
    [],
  );

  const { regionData: regionList } = UseRegionList();
  const [regionData, setRegionData] = useState<{ id: string; name: string }[]>(
    [],
  );
  const [firmOfficeIndex, setFirmOfficeIndex] = useState<
    Map<string, AccountingFirmOfficeGroup>
  >(new Map());
  const [expandedFirmIds, setExpandedFirmIds] = useState<Set<string>>(
    () => new Set(),
  );

  const toggleFirmExpanded = useCallback((firmId: string) => {
    const normalized = firmId.trim();
    if (!normalized) {
      return;
    }
    setExpandedFirmIds((prev) => {
      const next = new Set(prev);
      if (next.has(normalized)) {
        next.delete(normalized);
      }
      else {
        next.add(normalized);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    void fetchAccountingFirmOfficeIndex().then((index) => {
      if (!cancelled) {
        setFirmOfficeIndex(index);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [selectedYear]);

  const yearOptions = useMemo(
    () =>
      Array.from({ length: currentFiscalYear - 2001 }, (_, i) => {
        const startYear = currentFiscalYear - i;
        return {
          id: String(startYear),
          name: billingPeriodRangeLabel(startYear),
        };
      }),
    [currentFiscalYear],
  );

  const selectedBillingPeriodOption = useMemo(
    () => optionById(yearOptions, String(selectedYear)),
    [yearOptions, selectedYear],
  );

  const persistentFilter = useMemo(
    () => ({
      ...ACCOUNTING_TABLE_OFFICE_VISIBILITY_FILTER,
      accountingFiscalYear: {
        value: String(invoiceFiscalYear),
        comparisonType: "EQUAL",
      },
    }),
    [invoiceFiscalYear],
  );

  // Total-mode persistent filter: active membership, no FY date-window.
  const totalModePersistentFilter = useMemo(
    () => ({
      ...ACCOUNTING_TABLE_OFFICE_VISIBILITY_FILTER,
      membershipCategory: { value: "active", comparisonType: "EQUAL" },
    }),
    [],
  );

  useEffect(() => {
    setRegionData((regionList ?? []).map((r) => ({ id: r.id, name: r.name })));
  }, [regionList]);

  const accountingActionsCell = useCallback(
    ({ row }: IHeaderCellProps) => {
      if (!row._hasChildren || !row._displayFirmId) {
        return null;
      }
      const firmId = String(row._displayFirmId);
      const isExpanded = expandedFirmIds.has(firmId);
      return (
        <button
          type="button"
          onClick={() => toggleFirmExpanded(firmId)}
          className={MANAGE_FIRM_EXPAND_JURISDICTIONS_BUTTON_CLASS}
          aria-label={
            isExpanded ? "Collapse jurisdictions" : "Expand jurisdictions"
          }
        >
          <ManageFirmJurisdictionsExpandGlyph expanded={isExpanded} />
        </button>
      );
    },
    [expandedFirmIds, toggleFirmExpanded],
  );

  const accountingFirmCell = useCallback(
    ({ row }: IHeaderCellProps) => {
      const isChild = row._rowKind === "groupChild";
      const isParent = row._rowKind === "groupParent";

      return (
        <div className="min-w-0">
          <div className={isParent ? "font-semibold" : ""}>
            {isChild ? (
              <span className="mr-1 text-gray-font" aria-hidden="true">
                └
              </span>
            ) : null}
            <Link
              to={`/accounting/${row.id}`}
              state={{ fromTab: activeTab }}
              className="text-[#0070CD] hover:underline"
            >
              {row.firm}
            </Link>
          </div>
        </div>
      );
    },
    [activeTab],
  );

  const accountingJurisdictionCell = useCallback(
    ({ row }: IHeaderCellProps) => {
      const firmId = String(row.firmId ?? "");
      const group = firmOfficeIndex.get(firmId);
      return formatAccountingJurisdictionCell(row, group);
    },
    [firmOfficeIndex],
  );

  useEffect(() => {
    if (viewMode !== "fiscalYear") {
      return;
    }
    let cancelled = false;
    getInvoiceAggregatesByOffice(invoiceFiscalYear)
      .then((map) => {
        if (!cancelled) {
          setBillingByOffice(map);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setBillingByOffice({});
        }
      });
    return () => {
      cancelled = true;
    };
  }, [invoiceFiscalYear, viewMode]);

  useEffect(() => {
    if (viewMode !== "total") {
      return;
    }
    let cancelled = false;
    getInvoiceAggregatesByOffice()
      .then((map) => {
        if (!cancelled) {
          setBillingByOfficeTotal(map);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setBillingByOfficeTotal({});
        }
      });
    return () => {
      cancelled = true;
    };
  }, [viewMode]);

  useEffect(() => {
    if (!STATS_TABS.has(activeTab) || viewMode !== "fiscalYear") {
      return;
    }

    let cancelled = false;

    const run = async () => {
      const params = new URLSearchParams();
      const filterForStats = {
        ...serverFilter,
        ...ACCOUNTING_TABLE_OFFICE_VISIBILITY_FILTER,
        accountingFiscalYear: {
          value: String(invoiceFiscalYear),
          comparisonType: "EQUAL",
        },
      };
      params.append("filter", JSON.stringify(filterForStats));
      params.append("fields", JSON.stringify(["id"]));

      try {
        const response = await axios.Get(`${endpoint}?${params.toString()}`);
        if (cancelled) {
          return;
        }

        const rows: any[] = Array.isArray(response.data) ? response.data : [];
        const officeIds = rows
          .map((r) => r?.id)
          .filter((id): id is string => typeof id === "string")
          .filter((id) =>
            matchesAccountingPaymentStatusFilter(
              id,
              paymentStatus,
              billingByOffice,
            ),
          );

        setTotals(sumOfficeBillingTotals(billingByOffice, officeIds));
      } catch {
        if (!cancelled) {
          setTotals(EMPTY_TOTALS);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [
    endpoint,
    serverFilter,
    invoiceFiscalYear,
    activeTab,
    viewMode,
    billingByOffice,
    paymentStatus,
  ]);

  useEffect(() => {
    if (!STATS_TABS.has(activeTab) || viewMode !== "total") {
      return;
    }

    let cancelled = false;

    const run = async () => {
      const params = new URLSearchParams();
      params.append(
        "filter",
        JSON.stringify({
          ...ACCOUNTING_TABLE_OFFICE_VISIBILITY_FILTER,
          membershipCategory: { value: "active", comparisonType: "EQUAL" },
        }),
      );
      params.append("fields", JSON.stringify(["id"]));

      try {
        const response = await axios.Get(`${endpoint}?${params.toString()}`);
        if (cancelled) {
          return;
        }

        const rows: any[] = Array.isArray(response.data) ? response.data : [];
        const officeIds = rows
          .map((r) => r?.id)
          .filter((id): id is string => typeof id === "string")
          .filter((id) =>
            matchesAccountingPaymentStatusFilter(
              id,
              paymentStatus,
              billingByOfficeTotal,
            ),
          );

        setTotalModeTotals(
          sumOfficeBillingTotals(billingByOfficeTotal, officeIds),
        );
      } catch {
        if (!cancelled) {
          setTotalModeTotals(EMPTY_TOTALS);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [endpoint, activeTab, viewMode, billingByOfficeTotal, paymentStatus]);

  // Fiscal Year mode columns.
  const fyColumnsDef = useMemo(() => {
    const allColumns: IHeader[] = [
      {
        id: "actions",
        header: "Action",
        width: 72,
        enableSorting: false,
        columnOrderFixed: true,
        defaultVisibility: true,
        cell: accountingActionsCell,
      },
      { id: "shortId", header: "Ref#", columnOrderFixed: true },
      {
        id: "firm",
        header: "Firm",
        width: 180,
        cell: accountingFirmCell,
        columnOrderFixed: true,
      },
      {
        id: "jurisdiction",
        header: "Jurisdiction",
        cell: accountingJurisdictionCell,
      },
      { id: "dateJoined", header: "Joined" },
      { id: "billingPeriod", ...BILLING_PERIOD_HEADER },
      { id: "invoiceDate", header: "Invoice Date" },
      {
        id: "initiationFeesInvoiced",
        header: "Invoiced",
        ...INITIATION_FEES_HEADER_GROUP,
      },
      {
        id: "initiationFeesPaid",
        header: "Paid",
        ...PAID_AMOUNT_COLUMN,
        ...INITIATION_FEES_HEADER_GROUP,
      },
      {
        id: "annualDuesInvoiced",
        header: "Invoiced",
        ...ANNUAL_DUES_HEADER_GROUP,
      },
      {
        id: "annualDuesPaid",
        header: "Paid",
        ...PAID_AMOUNT_COLUMN,
        ...ANNUAL_DUES_HEADER_GROUP,
      },
      {
        id: "specialFundInvoiced",
        header: "Invoiced",
        ...SPECIAL_FUND_HEADER_GROUP,
      },
      {
        id: "specialFundPaid",
        header: "Paid",
        ...PAID_AMOUNT_COLUMN,
        ...SPECIAL_FUND_HEADER_GROUP,
      },
      {
        id: "annualMeetingRegInvoiced",
        header: "Invoiced",
        ...ANNUAL_MEETING_HEADER_GROUP,
      },
      {
        id: "annualMeetingRegPaid",
        header: "Paid",
        ...PAID_AMOUNT_COLUMN,
        ...ANNUAL_MEETING_HEADER_GROUP,
      },
      {
        id: "lateFeesInvoiced",
        header: "Invoiced",
        ...LATE_FEES_HEADER_GROUP,
      },
      {
        id: "lateFeesPaid",
        header: "Paid",
        ...PAID_AMOUNT_COLUMN,
        ...LATE_FEES_HEADER_GROUP,
      },
      {
        id: "assessmentDuesInvoiced",
        header: "Invoiced",
        ...ASSESSMENT_DUES_HEADER_GROUP,
      },
      {
        id: "assessmentDuesPaid",
        header: "Paid",
        ...PAID_AMOUNT_COLUMN,
        ...ASSESSMENT_DUES_HEADER_GROUP,
      },
      { id: "paymentStatus", header: "Status" },
      { id: "comments", header: "Comments", enableSorting: false },
    ];

    return filterColumnsForTab(allColumns, activeTab);
  }, [
    activeTab,
    accountingActionsCell,
    accountingFirmCell,
    accountingJurisdictionCell,
  ]);

  // Total mode columns — no Invoice Date / Status; adds Balance.
  const totalColumnsDef = useMemo(() => {
    const allColumns: IHeader[] = [
      {
        id: "actions",
        header: "Action",
        width: 72,
        enableSorting: false,
        columnOrderFixed: true,
        defaultVisibility: true,
        cell: accountingActionsCell,
      },
      { id: "shortId", header: "Ref#", columnOrderFixed: true },
      {
        id: "firm",
        header: "Firm",
        width: 180,
        cell: accountingFirmCell,
        columnOrderFixed: true,
      },
      {
        id: "jurisdiction",
        header: "Jurisdiction",
        cell: accountingJurisdictionCell,
      },
      { id: "dateJoined", header: "Joined" },
      { id: "billingPeriod", ...BILLING_PERIOD_HEADER },
      {
        id: "initiationFeesInvoiced",
        header: "Invoiced",
        ...INITIATION_FEES_HEADER_GROUP,
      },
      {
        id: "initiationFeesPaid",
        header: "Paid",
        ...PAID_AMOUNT_COLUMN,
        ...INITIATION_FEES_HEADER_GROUP,
      },
      {
        id: "annualDuesInvoiced",
        header: "Invoiced",
        ...ANNUAL_DUES_HEADER_GROUP,
      },
      {
        id: "annualDuesPaid",
        header: "Paid",
        ...PAID_AMOUNT_COLUMN,
        ...ANNUAL_DUES_HEADER_GROUP,
      },
      {
        id: "specialFundInvoiced",
        header: "Invoiced",
        ...SPECIAL_FUND_HEADER_GROUP,
      },
      {
        id: "specialFundPaid",
        header: "Paid",
        ...PAID_AMOUNT_COLUMN,
        ...SPECIAL_FUND_HEADER_GROUP,
      },
      {
        id: "annualMeetingRegInvoiced",
        header: "Invoiced",
        ...ANNUAL_MEETING_HEADER_GROUP,
      },
      {
        id: "annualMeetingRegPaid",
        header: "Paid",
        ...PAID_AMOUNT_COLUMN,
        ...ANNUAL_MEETING_HEADER_GROUP,
      },
      {
        id: "lateFeesInvoiced",
        header: "Invoiced",
        ...LATE_FEES_HEADER_GROUP,
      },
      {
        id: "lateFeesPaid",
        header: "Paid",
        ...PAID_AMOUNT_COLUMN,
        ...LATE_FEES_HEADER_GROUP,
      },
      {
        id: "assessmentDuesInvoiced",
        header: "Invoiced",
        ...ASSESSMENT_DUES_HEADER_GROUP,
      },
      {
        id: "assessmentDuesPaid",
        header: "Paid",
        ...PAID_AMOUNT_COLUMN,
        ...ASSESSMENT_DUES_HEADER_GROUP,
      },
      { id: "balance", header: "Balance" },
      { id: "comments", header: "Comments", enableSorting: false },
    ];

    return filterColumnsForTab(allColumns, activeTab);
  }, [
    activeTab,
    accountingActionsCell,
    accountingFirmCell,
    accountingJurisdictionCell,
  ]);

  const filtersData: IFilterData[] = useMemo(
    () => [
      {
        name: "Fiscal Year",
        type: "custom",
        defaultCustomFilterValue: getDefaultAccountingBillingPeriodStartYear(),
        getCustomSelectedChips: () => [customFilterChip(selectedBillingPeriodOption)],
        render: ({ apply }) => (
          <DropDownMultiple<DropDownOptionBase>
            title="Fiscal Year:"
            options={yearOptions}
            value={selectedBillingPeriodOption}
            onChange={(val: DropDownOptionBase | null) => {
              if (!val) {
                return;
              }
              setSelectedYear(Number(val.id));
              apply(Number(val.id), null);
            }}
            searchable={false}
          />
        ),
      },
      {
        name: "Membership Status",
        fieldName: "membershipCategory",
        type: "custom",
        defaultCustomFilterValue: "active",
        getDefaultQueryClause: () => ({
          value: "active",
          comparisonType: "EQUAL",
        }),
        getCustomSelectedChips: () =>
          membershipCategory === "active"
            ? []
            : [customFilterChip(optionById(MEMBERSHIP_STATUS_OPTIONS, membershipCategory))],
        render: ({ apply }) => (
          <DropDownMultiple<DropDownOptionBase>
            title="Membership Status:"
            options={MEMBERSHIP_STATUS_OPTIONS}
            value={optionById(MEMBERSHIP_STATUS_OPTIONS, membershipCategory)}
            onChange={(val: DropDownOptionBase | null) => {
              const id = val ? String(val.id) : "all";
              setMembershipCategory(id);
              apply(id, { value: id, comparisonType: "EQUAL" });
            }}
            searchable={false}
          />
        ),
      },
      {
        name: "Payment Status",
        fieldName: "paymentStatus",
        type: "custom",
        defaultCustomFilterValue: "all",
        getCustomSelectedChips: () =>
          paymentStatus === "all"
            ? []
            : [customFilterChip(optionById(PAYMENT_STATUS_OPTIONS, paymentStatus))],
        render: ({ apply }) => (
          <DropDownMultiple<DropDownOptionBase>
            title="Payment Status:"
            options={PAYMENT_STATUS_OPTIONS}
            value={optionById(PAYMENT_STATUS_OPTIONS, paymentStatus)}
            onChange={(val: DropDownOptionBase | null) => {
              const id = val ? String(val.id) : "all";
              setPaymentStatus(id);
              apply(id, null);
            }}
            searchable={false}
          />
        ),
      },
      {
        name: "Billing Period",
        fieldName: "billingStartDate",
        type: "custom",
        defaultCustomFilterValue: "all",
        getCustomSelectedChips: () =>
          billingMonth === "all"
            ? []
            : [customFilterChip(optionById(BILLING_PERIOD_OPTIONS, billingMonth))],
        render: ({ apply }) => (
          <DropDownMultiple<DropDownOptionBase>
            title="Billing Period:"
            options={BILLING_PERIOD_OPTIONS}
            value={optionById(BILLING_PERIOD_OPTIONS, billingMonth)}
            onChange={(val: DropDownOptionBase | null) => {
              const id = val ? String(val.id) : "all";
              setBillingMonth(id);
              apply(
                id,
                id === "all"
                  ? null
                  : { value: id, comparisonType: "MONTH_EQUAL" },
              );
            }}
            searchable={false}
          />
        ),
      },
      {
        name: "Service Type",
        fieldName: "serviceType",
        objectData: SERVICE_TYPE_OPTIONS,
        comparison: "IN",
      },
      {
        name: "Region",
        fieldName: "regions",
        objectData: regionData,
        comparison: "BY_ID",
      },
    ],
    [
      regionData,
      selectedBillingPeriodOption,
      yearOptions,
      membershipCategory,
      paymentStatus,
      billingMonth,
    ],
  );

  const formatData = useCallback(
    (data: any[]) => {
      if (!data || !Array.isArray(data)) {
        return [];
      }

      return data.map((d) => {
        const cats = billingByOffice[d.id];
        const totals = cats?.totals;

        return {
          ...d,
          firmId: String(d.firm ?? "").trim(),
          membershipType: d.membershipType ?? "",
          shortId: d.shortId ?? d.legacyId ?? "",
          firm: officeFirmShortLabel(d),
          jurisdiction: resolveFirmJurisdictionDisplay(d.jurisdiction, d),
          dateJoined: d.dateJoined
            ? formatCalendarDateToString(d.dateJoined)
            : "",
          billingPeriod: formatBillingPeriod(d.billingStartDate),
          invoiceDate: cats?.invoiceDate
            ? formatCalendarDateToString(cats.invoiceDate)
            : "",
          initiationFeesInvoiced: formatAccountingAmount(
            totals?.initiation.invoiced,
          ),
          initiationFeesPaid: formatAccountingAmount(totals?.initiation.paid),
          assessmentDuesInvoiced: formatAccountingAmount(
            totals?.assessmentDues.invoiced,
          ),
          assessmentDuesPaid: formatAccountingAmount(
            totals?.assessmentDues.paid,
          ),
          lateFeesInvoiced: formatAccountingAmount(totals?.lateFees.invoiced),
          lateFeesPaid: formatAccountingAmount(totals?.lateFees.paid),
          paymentStatus: formatBillingStatusLabel(cats?.status),
          comments: formatAccountingComments(
            cats?.comments,
            d.dateWithdrawn,
            d.membershipStatus,
          ),
          annualDuesInvoiced: formatAccountingAmount(
            totals?.annualDues.invoiced,
          ),
          annualDuesPaid: formatAccountingAmount(totals?.annualDues.paid),
          specialFundInvoiced: formatAccountingAmount(
            totals?.specialFund.invoiced,
          ),
          specialFundPaid: formatAccountingAmount(totals?.specialFund.paid),
          annualMeetingRegInvoiced: formatAccountingAmount(
            totals?.annualMeeting.invoiced,
          ),
          annualMeetingRegPaid: formatAccountingAmount(
            totals?.annualMeeting.paid,
          ),
        };
      });
    },
    [billingByOffice],
  );

  const formatTotalData = useCallback(
    (data: any[]) => {
      if (!data || !Array.isArray(data)) {
        return [];
      }

      return data.map((d) => {
        const cats = billingByOfficeTotal[d.id];
        const totals = cats?.totals;

        const totalInvoiced = totals
          ? totals.initiation.invoiced +
            totals.annualDues.invoiced +
            totals.assessmentDues.invoiced +
            totals.membershipBalance.invoiced +
            totals.specialFund.invoiced +
            totals.annualMeeting.invoiced +
            totals.lateFees.invoiced
          : 0;
        const totalPaid = totals
          ? totals.initiation.paid +
            totals.annualDues.paid +
            totals.assessmentDues.paid +
            totals.membershipBalance.paid +
            totals.specialFund.paid +
            totals.annualMeeting.paid +
            totals.lateFees.paid
          : 0;
        const balance = totalPaid - totalInvoiced;

        return {
          ...d,
          firmId: String(d.firm ?? "").trim(),
          membershipType: d.membershipType ?? "",
          shortId: d.shortId ?? d.legacyId ?? "",
          firm: officeFirmShortLabel(d),
          jurisdiction: resolveFirmJurisdictionDisplay(d.jurisdiction, d),
          dateJoined: d.dateJoined
            ? formatCalendarDateToString(d.dateJoined)
            : "",
          billingPeriod: formatBillingPeriod(d.billingStartDate),
          initiationFeesInvoiced: formatAccountingAmount(
            totals?.initiation.invoiced,
          ),
          initiationFeesPaid: formatAccountingAmount(totals?.initiation.paid),
          assessmentDuesInvoiced: formatAccountingAmount(
            totals?.assessmentDues.invoiced,
          ),
          assessmentDuesPaid: formatAccountingAmount(
            totals?.assessmentDues.paid,
          ),
          annualDuesInvoiced: formatAccountingAmount(
            totals?.annualDues.invoiced,
          ),
          annualDuesPaid: formatAccountingAmount(totals?.annualDues.paid),
          specialFundInvoiced: formatAccountingAmount(
            totals?.specialFund.invoiced,
          ),
          specialFundPaid: formatAccountingAmount(totals?.specialFund.paid),
          annualMeetingRegInvoiced: formatAccountingAmount(
            totals?.annualMeeting.invoiced,
          ),
          annualMeetingRegPaid: formatAccountingAmount(
            totals?.annualMeeting.paid,
          ),
          lateFeesInvoiced: formatAccountingAmount(totals?.lateFees.invoiced),
          lateFeesPaid: formatAccountingAmount(totals?.lateFees.paid),
          balance: balance !== 0 ? balance : "",
          comments: formatAccountingComments(
            cats?.comments,
            d.dateWithdrawn,
            d.membershipStatus,
          ),
        };
      });
    },
    [billingByOfficeTotal],
  );

  const formatDataGrouped = useCallback(
    (data: any[]) => {
      // Payment status is derived from billing aggregates (not an office field).
      const filtered = filterAccountingRawDataByPaymentStatus(
        data,
        paymentStatus,
        billingByOffice,
      );
      const sorted = sortAccountingRawData(
        filtered,
        activeSorting,
        billingByOffice,
      );
      const formatted = formatData(sorted) as AccountingTableRow[];
      return buildAccountingTableDisplayRows(
        formatted,
        firmOfficeIndex,
        expandedFirmIds,
      );
    },
    [
      formatData,
      firmOfficeIndex,
      expandedFirmIds,
      activeSorting,
      billingByOffice,
      paymentStatus,
    ],
  );

  const formatTotalDataGrouped = useCallback(
    (data: any[]) => {
      const filtered = filterAccountingRawDataByPaymentStatus(
        data,
        paymentStatus,
        billingByOfficeTotal,
      );
      const sorted = sortAccountingRawData(
        filtered,
        activeSorting,
        billingByOfficeTotal,
      );
      const formatted = formatTotalData(sorted) as AccountingTableRow[];
      return buildAccountingTableDisplayRows(
        formatted,
        firmOfficeIndex,
        expandedFirmIds,
      );
    },
    [
      formatTotalData,
      firmOfficeIndex,
      expandedFirmIds,
      activeSorting,
      billingByOfficeTotal,
      paymentStatus,
    ],
  );

  const formatExcelData = useCallback(
    (data: any[]) =>
      formatData(
        filterAccountingRawDataByPaymentStatus(
          data,
          paymentStatus,
          billingByOffice,
        ),
      ),
    [formatData, paymentStatus, billingByOffice],
  );

  const formatTotalExcelData = useCallback(
    (data: any[]) =>
      formatTotalData(
        filterAccountingRawDataByPaymentStatus(
          data,
          paymentStatus,
          billingByOfficeTotal,
        ),
      ),
    [formatTotalData, paymentStatus, billingByOfficeTotal],
  );

  const activeTotals = viewMode === "total" ? totalModeTotals : totals;

  const statsContent =
    showStats && STATS_TABS.has(activeTab) ? (
      <AccountingStats
        totals={activeTotals}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />
    ) : undefined;

  return (
    <div className="flex flex-col min-w-0">
      <QuickFiltersBar
        quickFilters={ACCOUNTING_TABS}
        selectedQuickFilter={activeTab}
        quickFilterTotalCount={{}}
        onQuickFilterClick={(key) => setActiveTab(key as AccountingTab)}
        showCounts={false}
        lockActiveQuickFilter
        sticky
        quickFilterBarAccessory={
          <div className="flex flex-wrap items-center gap-2">
            {STATS_TABS.has(activeTab) && (
              <Button
                text={showStats ? "Hide Stats" : "Show Stats"}
                color="white"
                variant="outlined"
                size="fixed"
                onClick={() => setShowStats((prev) => !prev)}
                icon={<Stats />}
              />
            )}
            <Button
              text="Manage Fiscal Year"
              color="white"
              variant="outlined"
              size="fixed"
              icon={<Gear />}
              onClick={() => setManageFiscalYearOpen(true)}
            />
          </div>
        }
      />

      {viewMode === "fiscalYear" ? (
        <FilteredTableCore
          columnsDef={fyColumnsDef}
          showRowNumber
          showTopHorizontalScrollBar
          endpoint={endpoint}
          filtersData={filtersData}
          formatData={formatDataGrouped}
          formatExcelData={formatExcelData}
          persistentFilter={persistentFilter}
          bannerContent={statsContent}
          onQueryFilterChange={setServerFilter}
          onPanelFilterReset={handlePanelFilterReset}
          onSortingChange={setActiveSorting}
          defaultSorting={{ field: "jurisdiction", order: "asc" }}
          defaultPageSize={200}
          persistPageSize={false}
          showSearchInput
          searchPlaceholder="Search by Office or Ref#"
          excelFilename="accounting_offices"
        />
      ) : (
        <FilteredTableCore
          columnsDef={totalColumnsDef}
          showRowNumber
          showTopHorizontalScrollBar
          endpoint={endpoint}
          filtersData={[]}
          formatData={formatTotalDataGrouped}
          formatExcelData={formatTotalExcelData}
          persistentFilter={totalModePersistentFilter}
          bannerContent={statsContent}
          onSortingChange={setActiveSorting}
          defaultSorting={{ field: "jurisdiction", order: "asc" }}
          defaultPageSize={200}
          persistPageSize={false}
          showSearchInput
          searchPlaceholder="Search by Office or Ref#"
          excelFilename="accounting_offices_total"
        />
      )}

      <ManageFiscalYearModal
        isOpen={manageFiscalYearOpen}
        onClose={() => setManageFiscalYearOpen(false)}
      />
    </div>
  );
}
